import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useTranslation, parseApiError, useSettings } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Loader2, Mail } from 'lucide-react';
import Pagination from '../components/Pagination';

const MembersPage = () => {
  const t = useTranslation();
  const { settings } = useSettings();
  
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [inviteData, setInviteData] = useState({ email: '', name: '', role_id: '' });
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [membersRes, rolesRes] = await Promise.all([
        axios.get(`${API}/members`, { params: { page, page_size: 10 } }),
        axios.get(`${API}/roles`, { params: { page_size: 100 } })
      ]);
      setMembers(membersRes.data.items);
      setTotalPages(membersRes.data.total_pages);
      setRoles(rolesRes.data.items);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteData.email.trim() || !inviteData.name.trim() || !inviteData.role_id) {
      toast.error('All fields are required');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/members/invite`, inviteData);
      toast.success('Invitation sent');
      setInviteDialogOpen(false);
      setInviteData({ email: '', name: '', role_id: '' });
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error) || 'Failed to invite');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingMember) return;

    setSaving(true);
    try {
      await axios.put(`${API}/members/${editingMember.id}`, {
        name: editingMember.name,
        role_id: editingMember.role_id,
        is_active: editingMember.is_active
      });
      toast.success('Member updated');
      setEditDialogOpen(false);
      setEditingMember(null);
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error) || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/members/${deleteId}`);
      toast.success('Member deleted');
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error) || 'Failed to delete');
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="members-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 font-['Outfit'] tracking-tight">
            {t('members')}
          </h1>
          <p className="text-gray-500 mt-1">Manage team members and their roles</p>
        </div>
        <Button 
          onClick={() => setInviteDialogOpen(true)}
          className="gap-2"
          data-testid="invite-member-btn"
        >
          <Plus size={18} />
          {t('inviteMember')}
        </Button>
      </div>

      {/* Table */}
      <Card className="border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">{t('noResults')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-gray-600">{t('name')}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t('email')}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t('role')}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t('status')}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t('createdAt')}</TableHead>
                  <TableHead className="text-right font-semibold text-gray-600">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {member.name}
                        {member.is_superadmin && (
                          <Badge variant="secondary" className="text-xs bg-primary text-primary-foreground">
                            Super Admin
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>
                      <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                        {member.role_name || 'No Role'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        member.is_active 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {member.is_active ? t('active') : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>{new Date(member.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(member)}
                          data-testid={`edit-member-${member.id}`}
                        >
                          <Edit size={16} />
                        </Button>
                        {!member.is_superadmin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteId(member.id)}
                            data-testid={`delete-member-${member.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">{t('inviteMember')}</DialogTitle>
            <DialogDescription>
              Send an invitation to a new team member
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('fullName')}</Label>
              <Input
                value={inviteData.name}
                onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                placeholder="John Doe"
                data-testid="invite-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  placeholder="john@example.com"
                  className="pl-10"
                  data-testid="invite-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('role')}</Label>
              <Select 
                value={inviteData.role_id} 
                onValueChange={(v) => setInviteData({ ...inviteData, role_id: v })}
              >
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleInvite} 
              disabled={saving}
              data-testid="send-invite-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('invite')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Edit Member</DialogTitle>
            <DialogDescription>
              Update member details and permissions
            </DialogDescription>
          </DialogHeader>

          {editingMember && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t('fullName')}</Label>
                <Input
                  value={editingMember.name}
                  onChange={(e) => setEditingMember({ ...editingMember, name: e.target.value })}
                  data-testid="edit-member-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>{t('email')}</Label>
                <Input
                  value={editingMember.email}
                  disabled
                  className="bg-gray-50"
                />
              </div>

              <div className="space-y-2">
                <Label>{t('role')}</Label>
                <Select 
                  value={editingMember.role_id || ''} 
                  onValueChange={(v) => setEditingMember({ ...editingMember, role_id: v })}
                >
                  <SelectTrigger data-testid="edit-member-role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>{t('active')}</Label>
                  <p className="text-sm text-gray-500">Enable or disable account access</p>
                </div>
                <Switch
                  checked={editingMember.is_active}
                  onCheckedChange={(checked) => setEditingMember({ ...editingMember, is_active: checked })}
                  data-testid="edit-member-active-switch"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleUpdate} 
              disabled={saving}
              data-testid="update-member-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this member from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MembersPage;
